import { useMemo } from "react";
import DramaCard from "@/components/feature/DramaCard";
import { dramas, dramaGenres } from "@/mocks/dramas";
import type { Drama } from "@/pages/home/types";

interface GenreSectionProps {
  query: string;
  onQueryChange: (value: string) => void;
  genre: string;
  onGenreChange: (value: string) => void;
}

export default function GenreSection({ query, onQueryChange, genre, onGenreChange }: GenreSectionProps) {
  const trimmed = query.trim().toLowerCase();

  const filtered = useMemo(() => {
    return (dramas as Drama[]).filter((d) => {
      const matchGenre = genre === "全部" || d.genres.includes(genre);
      if (!matchGenre) return false;
      if (!trimmed) return true;
      const haystack = `${d.title} ${d.originalTitle} ${d.cast} ${d.genres.join(" ")} ${d.summary}`.toLowerCase();
      return haystack.includes(trimmed);
    });
  }, [genre, trimmed]);

  return (
    <section id="genres" className="w-full bg-background-50 py-16 md:py-20">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-6">
        <div>
          <span className="font-label text-sm font-medium text-primary-600">按口味锁定</span>
          <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            按题材选剧 · 筛出你的下一部
          </h2>
          <p className="mt-3 max-w-2xl font-body text-sm leading-relaxed text-foreground-600">
            喜欢甜宠爱情，还是烧脑悬疑？点击题材标签即时筛选，也可以直接输入剧名、演员或关键词。
            筛选只作用于本页已收录的剧集。
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <i
              className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"
              aria-hidden="true"
            ></i>
            <input
              type="search"
              name="genre-search"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="输入剧名、演员或题材关键词"
              aria-label="筛选剧集"
              className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-10 pr-4 font-body text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-400/30"
            />
          </div>
          <p className="font-label text-sm text-foreground-600">
            共筛选出 <span className="font-heading text-lg font-bold text-primary-600">{filtered.length}</span> 部剧集
          </p>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {dramaGenres.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => onGenreChange(g)}
              aria-pressed={genre === g}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 font-label text-sm transition-colors ${
                genre === g
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
              }`}
            >
              {g}
            </button>
          ))}
        </div>

        {(trimmed || genre !== "全部") && (
          <button
            type="button"
            onClick={() => {
              onQueryChange("");
              onGenreChange("全部");
            }}
            className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 px-3 py-1.5 font-label text-sm text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-refresh-line" aria-hidden="true"></i>
            重置筛选条件
          </button>
        )}

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
            {filtered.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-emotion-sad-line text-2xl" aria-hidden="true"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              没有找到符合条件的剧集
            </p>
            <p className="mt-1 font-body text-sm text-foreground-600">
              试试换个题材标签，或缩短搜索关键词。
            </p>
          </div>
        )}
      </div>
    </section>
  );
}